import { memo, useState, useEffect, useRef } from 'react';
import {
  IconButton,
  Chip,
  Box,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import type { BoardColumnCoreFragment } from '../../../generated/graphql';

interface BoardColumnHeaderProps {
  column: BoardColumnCoreFragment;
  cardCount: number;
  onRename: (newName: string) => void;
  /** Omit to hide the Delete menu item (e.g., when edit mode is off). */
  onDelete?: () => void;
}

function BoardColumnHeaderImpl({
  column,
  cardCount,
  onRename,
  onDelete,
}: BoardColumnHeaderProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(column.name);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => setDraft(column.name), [column.name]);
  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const commit = () => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed && trimmed !== column.name) onRename(trimmed);
    else setDraft(column.name);
  };

  const closeMenu = () => setMenuAnchor(null);
  const handleDelete = () => {
    closeMenu();
    onDelete?.();
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      px={1.5}
      py={1}
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1,
        backgroundColor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
        minHeight: 48,
        // `gap`, not Stack's `spacing` prop: spacing emits a
        // `> :not(style) + :not(style)` margin-left rule whose specificity
        // beats an `sx` class on a child, which silently overrode the
        // `ml: 'auto'` that is meant to push the actions to the right edge.
        gap: 1,
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          flexShrink: 0,
        }}
      />
      {editing ? (
        <TextField
          inputRef={inputRef}
          size="small"
          sx={{ flexGrow: 1, minWidth: 0 }}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') {
              setEditing(false);
              setDraft(column.name);
            }
          }}
        />
      ) : (
        <Typography
          variant="subtitle2"
          noWrap
          sx={{
            cursor: onDelete ? 'pointer' : 'default',
            fontWeight: 600,
            // Take the free space so the actions sit at the right edge, and
            // allow shrinking so a long column name ellipsises instead of
            // pushing the kebab out of the fixed-width column.
            flexGrow: 1,
            minWidth: 0,
          }}
          onClick={onDelete ? () => setEditing(true) : undefined}
        >
          {column.name}
        </Typography>
      )}
      <Chip
        variant="soft"
        size="small"
        label={cardCount}
        aria-label={`${cardCount} projects`}
        sx={{ height: 20, minWidth: 24, fontSize: 11 }}
      />
      {onDelete && (
        <Stack direction="row" sx={{ flexShrink: 0 }}>
          <IconButton
            size="small"
            aria-label="column actions"
            sx={{
              minWidth: { xs: 44, sm: 'auto' },
              minHeight: { xs: 44, sm: 'auto' },
            }}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={closeMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={handleDelete}>Delete column</MenuItem>
          </Menu>
        </Stack>
      )}
    </Stack>
  );
}

export const BoardColumnHeader = memo(BoardColumnHeaderImpl);
